import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import { toast } from 'react-toastify';
import { Edit, Mail, Search, User, ChevronLeft, ChevronRight, Eye, RefreshCw, AlertCircle } from 'lucide-react';
import api from '../components/api';
import Modal from '../components/Modal';
import FormInput from '../components/FormInput';
import { formatDate, formatPhoneNumber } from '../utils/format';
import { useNavigate } from 'react-router-dom';

const useInitialData = (currentPage, pageSize, selectedAnnee, searchTerm) => {
  const [data, setData] = useState({
    parents: [],
    totalPages: 1,
    totalItems: 0,
    loading: false,
    anneesAcademiques: []
  });

  const fetchData = useCallback(async () => {
    try {
      setData(prev => ({ ...prev, loading: true }));
      
      // Récupérer les parents avec filtres
      const parentsResponse = await api.get(`/etudiant/liste-parents/`, {
        params: {
          page: currentPage,
          page_size: pageSize,
          annee: selectedAnnee || '',
          search: searchTerm || ''
        }
      });

      // Récupérer les années académiques
      const anneesResponse = await api.get('/etablissement/annees-academiques/');
      
      // Traiter la réponse même si elle est vide
      const parentsData = parentsResponse.data?.data || [];
      const totalPages = parentsResponse.data?.total_pages || 0;
      const totalItems = parentsResponse.data?.count || 0;
      
      setData({
        parents: parentsData,
        totalPages: totalPages,
        totalItems: totalItems,
        loading: false,
        anneesAcademiques: anneesResponse.data || []
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des parents:', error);
      
      // Ne pas traiter les 404 comme des erreurs critiques
      if (error.response && error.response.status === 404) {
        setData({
          parents: [],
          totalPages: 0,
          totalItems: 0,
          loading: false,
          anneesAcademiques: []
        });
      } else {
        toast.error('Erreur lors de la récupération des parents');
        setData(prev => ({ ...prev, loading: false }));
      }
    }
  }, [currentPage, pageSize, selectedAnnee, searchTerm]);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, selectedAnnee, searchTerm, fetchData]);

  return {
    ...data,
    fetchData
  };
};

const ParentPage = () => {
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedParent, setSelectedParent] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [selectedAnnee, setSelectedAnnee] = useState('');

    const { parents, totalPages, totalItems, loading, anneesAcademiques, fetchData } = useInitialData(currentPage, pageSize, selectedAnnee, searchTerm);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleAnneeChange = (e) => {
        setSelectedAnnee(e.target.value);
        setCurrentPage(1);
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleEdit = (parent) => {
        setSelectedParent(parent);
        setIsModalOpen(true);
    };

    const handleViewDetails = (parent) => {
        navigate(`/parent/${parent.id}`);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (selectedParent) {
                await api.put(`/etudiant/details-parents/${selectedParent.id}/`, selectedParent);
                toast.success('Parent modifié avec succès');
            }
            setIsModalOpen(false);
            setSelectedParent(null);
            fetchData();
        } catch (error) {
            console.error('Erreur lors de la modification du parent:', error);
            toast.error('Erreur lors de la modification du parent');
        }
    };

    const filteredParents = parents || [];

    const Pagination = () => (
        <div className="flex items-center justify-between mt-6">
            <div className="flex-1 flex justify-between sm:hidden">
                <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                        currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                    Précédent
                </button>
                <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                        currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                    Suivant
                </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                    <p className="text-sm text-gray-700">
                        Affichage de{' '}
                        <span className="font-medium">
                            {((currentPage - 1) * pageSize) + 1}
                        </span>{' '}
                        à{' '}
                        <span className="font-medium">
                            {Math.min(currentPage * pageSize, totalItems)}
                        </span>{' '}
                        sur{' '}
                        <span className="font-medium">{totalItems}</span> parents
                    </p>
                </div>
                <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                                currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                            <span className="sr-only">Précédent</span>
                            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                        </button>
                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i + 1}
                                onClick={() => handlePageChange(i + 1)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                    currentPage === i + 1
                                        ? 'z-10 bg-[#202654] border-[#202654] text-white'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                            >
                                {i + 1}
                            </button>
                        ))}
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                                currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                            <span className="sr-only">Suivant</span>
                            <ChevronRight className="h-5 w-5" aria-hidden="true" />
                        </button>
                    </nav>
                </div>
            </div>
        </div>
    );

    return (
        <Layout>
            <div className="min-h-screen bg-gray-50">
            <div className="container mx-auto px-4 py-8">
                    {/* Card principale avec design professionnel */}
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                        {/* Header avec bannière bleue */}
                        <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-8">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                                <div className="flex items-center text-white">
                                    <button
                                        onClick={() => navigate(-1)}
                                        className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-5 w-5"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </button>
                    <div>
                                        <h1 className="text-2xl font-bold">Gestion des Parents</h1>
                                        <p className="mt-1 text-blue-100">
                                            Gérez et visualisez tous les parents de l'établissement
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-4 md:mt-0 flex flex-col sm:flex-row sm:space-x-3 space-y-3 sm:space-y-0">
                                    <button
                                        onClick={() => fetchData()}
                                        className="inline-flex items-center justify-center px-4 py-2 border border-white/20 rounded-md shadow-sm text-sm font-medium text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-200"
                                    >
                                        <RefreshCw className="h-4 w-4 mr-2" />
                                        Actualiser
                                    </button>
                                </div>
                    </div>
                </div>

                        {/* Filtres */}
                        <div className="p-6 border-b border-gray-200 bg-gray-50">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                <div className="relative w-64">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Search className="h-5 w-5 text-gray-400" />
                                    </div>
                                <input
                                    type="text"
                                    placeholder="Rechercher un parent..."
                                    value={searchTerm}
                                        onChange={handleSearch}
                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                                />
                            </div>
                            
                            <div className="flex gap-4">
                                <select
                                    value={selectedAnnee}
                                    onChange={handleAnneeChange}
                                    className="block w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                                >
                                    <option value="">Toutes les années académiques</option>
                                    {anneesAcademiques.map(annee => (
                                        <option key={annee.id} value={annee.id}>
                                            {annee.libelle}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                        {/* Contenu principal */}
                        <div className="p-6">
                    {loading ? (
                                <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#202654]"></div>
                        </div>
                    ) : (
                                <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Nom
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Prénom
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Contact
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Email
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Nombre d'étudiants
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredParents.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-12">
                                                <div className="flex flex-col items-center justify-center text-center">
                                                    <User className="h-12 w-12 text-gray-400 mb-4" />
                                                    <h3 className="text-lg font-medium text-gray-900 mb-1">Aucun parent trouvé</h3>
                                                    <p className="text-sm text-gray-500 max-w-md">
                                                        {searchTerm ? 'Essayez avec un autre terme de recherche' : 'Aucun parent enregistré'}
                                                    </p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredParents.map((parent) => (
                                            <tr key={parent.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {parent.nom_parent}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">
                                                        {parent.prenom_parent}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">
                                                        {formatPhoneNumber(parent.contact_parent)}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">
                                                        {parent.email_parent || '-'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">
                                                        {parent.nombre_etudiant}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex justify-end space-x-2">
                                                        <button
                                                            onClick={() => handleViewDetails(parent)}
                                                            className="text-indigo-600 hover:text-indigo-900"
                                                            title="Voir détails"
                                                        >
                                                            <Eye className="h-5 w-5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleEdit(parent)}
                                                            className="text-blue-600 hover:text-blue-900"
                                                            title="Modifier"
                                                        >
                                                            <Edit className="h-5 w-5" />
                                                        </button>
                                                        {parent.email_parent && (
                                                            <a
                                                                href={`mailto:${parent.email_parent}`}
                                                                className="text-green-600 hover:text-green-900"
                                                                title="Envoyer un email"
                                                            >
                                                                <Mail className="h-5 w-5" />
                                                            </a>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                                    {/* Pagination */}
                                    {totalPages > 1 && <Pagination />}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setSelectedParent(null);
                }}
                title="Modifier le parent"
            >
                <form onSubmit={handleSubmit} className="space-y-4">
                    <FormInput
                        label="Nom"
                        name="nom_parent"
                        value={selectedParent?.nom_parent || ''}
                        onChange={(e) => setSelectedParent({ ...selectedParent, nom_parent: e.target.value })}
                        required
                    />
                    <FormInput
                        label="Prénom"
                        name="prenom_parent"
                        value={selectedParent?.prenom_parent || ''}
                        onChange={(e) => setSelectedParent({ ...selectedParent, prenom_parent: e.target.value })}
                        required
                    />
                    <FormInput
                        label="Contact"
                        name="contact_parent"
                        value={selectedParent?.contact_parent || ''}
                        onChange={(e) => setSelectedParent({ ...selectedParent, contact_parent: e.target.value })}
                        required
                    />
                    <FormInput
                        label="Email"
                        name="email_parent"
                        type="email"
                        value={selectedParent?.email_parent || ''}
                        onChange={(e) => setSelectedParent({ ...selectedParent, email_parent: e.target.value })}
                    />
                    <FormInput
                        label="Adresse"
                        name="adresse_parent"
                        value={selectedParent?.adresse_parent || ''}
                        onChange={(e) => setSelectedParent({ ...selectedParent, adresse_parent: e.target.value })}
                    />
                    <div className="flex justify-end space-x-2">
                        <button
                            type="button"
                            onClick={() => {
                                setIsModalOpen(false);
                                setSelectedParent(null);
                            }}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                        >
                            Modifier
                        </button>
                    </div>
                </form>
            </Modal>
        </Layout>
    );
};

export default ParentPage;
